import React, { useEffect, useState } from 'react';
import AppLoaderGraph from '../../../../app/features/loader/graph/AppLoaderGraph';
import { TInvestExpertiseTag } from '../../types/expertise/TInvestExpertiseTag';
import * as am5 from '@amcharts/amcharts5';
import * as am5percent from '@amcharts/amcharts5/percent';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import am5themes_Dark from '@amcharts/amcharts5/themes/Dark';
import am5themes_Responsive from '@amcharts/amcharts5/themes/Responsive';
import { useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import am5locales_en_US from '@amcharts/amcharts5/locales/en_US';
import am5locales_fr_FR from '@amcharts/amcharts5/locales/fr_FR';
import useAppFormatAmount from '../../../../app/features/format/hooks/useAppFormatAmount';
import useAppFormatNumber from '../../../../app/features/format/hooks/useAppFormatNumber';
import _ from 'lodash';
import useInvestExpertises from '../../hooks/expertise/useInvestExpertises';
import { useAppSettings } from '../../../../app/features/settings/providers/AppSettingsProvider';
import { TInvestFundCurrency } from '../../types/fund/TInvestFundCurrency';
import langInvestDashboardMultiExpertises from '../../langs/langInvestDashboardMultiExpertises';

type CInvestDashboardGraphMultiExpertisesProps = {
	graphKpis: {
		callableCapital: number;
		netCalledCapital: number;
		committedCapital: number;
		tag: TInvestExpertiseTag;
		currency: TInvestFundCurrency;
	}[];
	selectedCurrency: TInvestFundCurrency;
};
const CInvestDashboardGraphMultiExpertises = (props: CInvestDashboardGraphMultiExpertisesProps) => {
	const { graphKpis, selectedCurrency } = props;
	const investExpertises = useInvestExpertises();

	const theme = useTheme();
	const { t, i18n } = useTranslation();
	const { rounding } = useAppSettings();
	const { displayRounding, displayRoundingSuffix, displayCurrencySymbol } = useAppFormatAmount();
	const { formatAmount } = useAppFormatNumber();

	const darkModeEnabled = theme.palette.mode === 'dark';

	const [graphKpisInCurrency, setGraphKpisInCurrency] = useState<typeof graphKpis>([]);
	useEffect(() => {
		if (graphKpis && selectedCurrency) {
			const x = graphKpis.filter((x) => x.currency === selectedCurrency);
			setGraphKpisInCurrency(x);
		}
	}, [graphKpis, selectedCurrency]);

	useEffect(() => {
		if (graphKpisInCurrency && investExpertises) {
			// Expertise chart
			const expertiseSynthesis = am5.Root.new('invest-dashboard-multi-expertises-chart');
			darkModeEnabled
				? expertiseSynthesis.setThemes([
						am5themes_Dark.new(expertiseSynthesis),
						am5themes_Responsive.new(expertiseSynthesis),
				  ])
				: expertiseSynthesis.setThemes([
						am5themes_Animated.new(expertiseSynthesis),
						am5themes_Responsive.new(expertiseSynthesis),
				  ]);

			if (i18n.language === 'fr') {
				expertiseSynthesis.locale = am5locales_fr_FR;
			} else {
				expertiseSynthesis.locale = am5locales_en_US;
			}

			const chartExpertise = expertiseSynthesis.container.children.push(
				am5percent.PieChart.new(expertiseSynthesis, {
					layout: expertiseSynthesis.verticalLayout,
					innerRadius: am5.percent(90),
				}),
			);

			const seriesExpertise = chartExpertise.series.push(
				am5percent.PieSeries.new(expertiseSynthesis, {
					valueField: 'value',
					categoryField: 'category',
				}),
			);

			seriesExpertise.labels.template.set('visible', false);
			seriesExpertise.ticks.template.set('visible', false);

			chartExpertise.seriesContainer.children.push(
				am5.Label.new(expertiseSynthesis, {
					textAlign: 'center',
					y: 26,
					centerY: am5.percent(100),
					fill: am5.color('#FFF'),
					centerX: am5.p50,
					text: `[bold fontSize:26px]${formatAmount(
						displayRounding(_.sumBy(graphKpisInCurrency, 'committedCapital'), rounding),
						2,
						true,
					)} ${displayRoundingSuffix(rounding)}${displayCurrencySymbol(selectedCurrency)}[/][/]`,
				}),
			);

			const sliceTemplate = seriesExpertise.slices.template;
			sliceTemplate.setAll({
				templateField: 'settings',
				cornerRadius: 30,
			});

			sliceTemplate.set('toggleKey', 'none');
			sliceTemplate.states.create('active', {
				shiftRadius: 0,
			});

			const sliceTicksTemplate = seriesExpertise.ticks.template;
			sliceTicksTemplate.setAll({
				templateField: 'tickSettings',
			});

			const sliceTooltipsTemplate = seriesExpertise.slices.template;
			sliceTooltipsTemplate.setAll({
				templateField: 'tooltipSettings',
			});

			seriesExpertise.data.setAll(
				graphKpisInCurrency.map((graphKpi) => ({
					category: t(langInvestDashboardMultiExpertises.committed_capital.tag),
					value: graphKpi.committedCapital,
					settings: {
						fill: investExpertises.find((x) => x.tag === graphKpi.tag)?.sliceColor.dark,
						stroke: 0,
					},
					tickSettings: {
						stroke: investExpertises.find((x) => x.tag === graphKpi.tag)?.sliceColor.dark,
						strokeOpacity: 1,
					},
					tooltipSettings: {
						fill: investExpertises.find((x) => x.tag === graphKpi.tag)?.sliceColor.dark,
						stroke: investExpertises.find((x) => x.tag === graphKpi.tag)?.sliceColor.dark,
						tooltipText:
							'[#1A2B48][bold]' +
							investExpertises.find((x) => x.tag === graphKpi.tag)?.name +
							`[/][/]${
								graphKpi.tag === 'listed_assets' // Actifs cotés => On affiche l'encours uniquement
									? `\n[#1A2B48]${t(
											langInvestDashboardMultiExpertises.graph_legend_outstanding.tag,
									  )} : ${formatAmount(
											displayRounding(graphKpi.committedCapital, rounding),
											2,
											true,
									  )} ${displayRoundingSuffix(rounding)}${displayCurrencySymbol(
											selectedCurrency,
									  )}[/]`
									: `\n[#1A2B48]${t(
											langInvestDashboardMultiExpertises.graph_legend_dry_powder.tag,
									  )} : ${formatAmount(
											displayRounding(graphKpi.callableCapital, rounding),
											2,
											true,
									  )} ${displayRoundingSuffix(rounding)}${displayCurrencySymbol(
											selectedCurrency,
									  )}[/]\n[#1A2B48]${t(
											langInvestDashboardMultiExpertises.graph_legend_net_called_capital.tag,
									  )} : ${formatAmount(
											displayRounding(graphKpi.netCalledCapital, rounding),
											2,
											true,
									  )} ${displayRoundingSuffix(rounding)}${displayCurrencySymbol(
											selectedCurrency,
									  )}[/]`
							}[/]`,
					},
				})),
			);

			// Play initial series animation
			seriesExpertise.appear(1000, 100);

			return () => {
				expertiseSynthesis.dispose();
			};
		}
	}, [
		darkModeEnabled,
		displayCurrencySymbol,
		displayRounding,
		displayRoundingSuffix,
		formatAmount,
		graphKpisInCurrency,
		i18n.language,
		investExpertises,
		rounding,
		theme.palette.mode,
		theme.palette.primary.main,
		selectedCurrency,
		t,
	]);

	return (
		<>
			{graphKpisInCurrency && investExpertises ? (
				<div
					id="invest-dashboard-multi-expertises-chart"
					style={{ height: '250px', margin: '0px', padding: '0px' }}
				/>
			) : (
				<AppLoaderGraph height={200} />
			)}
		</>
	);
};

export default CInvestDashboardGraphMultiExpertises;
